'use client';

import { useState } from 'react';
import { useUser } from '@/components/hook/user';
import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';
import Image from 'next/image';
import checkmarkIcon from 'public/img/icon/checkmark-pink.svg';
import closeIcon from 'public/img/icon/close.svg';

import './EllaFeedback.style.scss';
import { EllaProductData } from '../EllaCartProduct/EllaCartProduct';

interface EllaFeedbackProps {
  recommendationId: string;
  messages: Array<{ role: string; content: string }>;
  recommendations: string[];
  selectedProducts?: string[];
  availableProducts?: Array<EllaProductData>;
  recommendationPrompt?: string;
  onFeedbackSubmitted?: () => void;
}

export default function EllaFeedback({
  recommendationId,
  messages,
  recommendations,
  selectedProducts,
  availableProducts,
  recommendationPrompt,
  onFeedbackSubmitted
}: EllaFeedbackProps) {
  const { value: user } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFeedback = async (isPositive: boolean) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/ella/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recommendationId,
          userId: user?.id,
          isPositive,
          context: {
            messages,
            recommendations,
            selectedProducts,
            availableProducts,
            recommendationPrompt
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setSubmitted(true);
      onFeedbackSubmitted?.();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className='ella-feedback'>
        <Text
          variant='text-s'
          className='ella-feedback__thank-you'
        >
          Thank you for your feedback!
        </Text>
      </div>
    );
  }

  return (
    <div className='ella-feedback'>
      <Text
        variant='text-s'
        className='ella-feedback__prompt'
      >
        Were these recommendations helpful?
      </Text>
      <div className='ella-feedback__buttons'>
        <Button
          variant='light'
          onClick={() => handleFeedback(true)}
          disabled={isSubmitting}
          className='ella-feedback__button'
        >
          <Image
            src={checkmarkIcon}
            alt='Thumbs up'
            width={24}
            height={24}
            className='ella-feedback__icon'
          />
        </Button>
        <Button
          variant='light'
          onClick={() => handleFeedback(false)}
          disabled={isSubmitting}
          className='ella-feedback__button'
        >
          <Image
            src={closeIcon}
            alt='Thumbs down'
            width={24}
            height={24}
            className='ella-feedback__icon'
          />
        </Button>
      </div>
    </div>
  );
}
